import { useState, useEffect } from 'react';
import { cpoDashboardAPI } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Star, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function StarRating({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`w-3 h-3 ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
      ))}
    </div>
  );
}

export default function CPODashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cpoDashboardAPI.getData().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><div className="h-8 w-48 bg-muted rounded animate-pulse" /><div className="grid grid-cols-2 gap-4">{[1, 2].map(i => <div key={i} className="h-64 bg-muted rounded-xl animate-pulse" />)}</div></div>;

  return (
    <div data-testid="cpo-dashboard-page" className="space-y-6">
      <h1 className="cp-page-title flex items-center gap-2"><Users className="w-6 h-6 text-accent" /> CPO Dashboard</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top 5 Suppliers */}
        <div className="cp-chart-card animate-fade-in" data-testid="top-suppliers-table">
          <h3 className="text-sm font-heading font-semibold mb-4 text-green-600">Top 5 Suppliers</h3>
          <div className="cp-table-wrapper">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Quality</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.top_suppliers?.map((s, i) => (
                  <TableRow key={s.id || i}>
                    <TableCell className="font-mono text-sm">{i + 1}</TableCell>
                    <TableCell className="font-medium text-sm">{s.name}</TableCell>
                    <TableCell><span className="cp-badge-indigo text-xs">{s.sector}</span></TableCell>
                    <TableCell><StarRating value={s.data_quality} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Worst 5 Suppliers */}
        <div className="cp-chart-card animate-fade-in" data-testid="worst-suppliers-table">
          <h3 className="text-sm font-heading font-semibold mb-4 text-red-500">Bottom 5 Suppliers</h3>
          <div className="cp-table-wrapper">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Quality</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.worst_suppliers?.map((s, i) => (
                  <TableRow key={s.id || i}>
                    <TableCell className="font-mono text-sm">{i + 1}</TableCell>
                    <TableCell className="font-medium text-sm">{s.name}</TableCell>
                    <TableCell><span className="cp-badge-indigo text-xs">{s.sector}</span></TableCell>
                    <TableCell><StarRating value={s.data_quality} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Response Speed Chart */}
      <div className="cp-chart-card animate-fade-in" data-testid="response-speed-chart">
        <h3 className="text-sm font-heading font-semibold mb-4">Supplier Response Speed (days)</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.response_speed || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="supplier" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="response_days" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
